<template>
  <bm-dialog
    v-if="value"
    :visible="value"
    :label="dialogLabels.title"
    @accept="validateAndAccept"
    @cancel="cancel"
  >
    <bm-alert v-if="visitIsOnActiveJourney" type="warning" dismissible>
      {{ $m.distribution_visit_on_active_journey }}
    </bm-alert>
    <v-container pa-0 fluid>
      <v-row>
        <v-col class="pb-0" cols="6">
          <bm-dropdown
            v-model="selectedBusinessId"
            :items="allBusinesses"
            :t="$m.keys.distribution_outlet_supplier"
            item-text="businessName"
            item-value="id"
            :rules="[validOutletSupplierOrAddressRule]"
            :disabled="!!ullageHeader"
            data-qa="add_visit_select_business"
            clearable
            @input="selectOutletOrSupplier"
          />
        </v-col>
        <v-col class="pb-0" cols="6">
          <bm-date
            v-model="visit.date"
            :t="$m.keys.date"
            :events="isValidDeliveryEvent"
            :rules="[
              $ruleSet.required(),
              $ruleSet.dateAfterOrEqual(today, $m.today),
            ]"
          />
        </v-col>
        <v-col class="pt-0" cols="12">
          <bm-btn-expand
            v-model="showAddress"
            :more-text-override="$m.distribution_manage_address"
            block
          />
        </v-col>
      </v-row>
    </v-container>

    <!-- Address Section -->
    <v-slide-x-transition>
      <bm-form-flow-layout v-if="showAddress" cols="12" sm="6">
        <bm-text
          v-model="visit.businessName"
          :t="$m.keys.distribution_business_name"
          :rules="[validOutletSupplierOrAddressRule]"
        />
        <bm-text
          v-model="visit.contactName"
          :t="$m.keys.distribution_contact"
        />

        <bm-address
          :t="$m.keys.address"
          :address-line1.sync="visit.addressLine1"
          :address-line2.sync="visit.addressLine2"
          :address-line3.sync="visit.addressLine3"
          :address-line4.sync="visit.addressLine4"
          :address-line5.sync="visit.addressLine5"
          :address-line6.sync="visit.addressLine6"
          :address-postcode.sync="visit.addressPostcode"
        />
        <bm-telephone
          v-model="visit.telephoneNumber"
          :t="$m.keys.distribution_contact_telephone"
        />
      </bm-form-flow-layout>
    </v-slide-x-transition>

    <bm-form-flow-layout cols="6">
      <bm-time
        v-model="visit.startTime"
        :t="dialogLabels.timeFrom"
        :error-messages="deliveryTimeErrors"
        data-qa="add_visit_start_time"
        clearable
      />
      <bm-time
        v-model="visit.endTime"
        :t="dialogLabels.timeTo"
        data-qa="add_visit_end_time"
        clearable
      />
      <bm-dropdown
        v-if="showVehicleMenu"
        v-model="selectedVehicleId"
        :items="activeVehiclesToday"
        :t="$m.keys.distribution_vehicle"
        item-text="name"
        item-value="id"
        clearable
      />
      <bm-number
        v-model="visit.stopMinutes"
        :t="$m.keys.distribution_stop_time"
        :rules="[$ruleSet.notNegativeNumber()]"
        :min="0"
        integral
      />
      <bm-weight
        v-model="visit.weightToDeliverKg"
        :t="$m.keys.distribution_visit_weight"
      />
    </bm-form-flow-layout>
    <v-container fluid pa-0>
      <v-row>
        <v-col cols="12">
          <bm-text-area
            v-model="visit.reason"
            :t="dialogLabels.textArea"
            data-qa="add_visit_reason"
          />
        </v-col>
      </v-row>
    </v-container>
  </bm-dialog>
</template>

<script lang="ts">
import BmDialog from "@/components/dialogs/BmDialog.vue";
import BmAlert from "@/components/controls/BmAlert.vue";
import BmTelephone from "@/components/controls/input/specialised/BmTelephone.vue";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { PropType } from "vue";
import { watch, defineComponent, onBeforeMount, computed, ref } from "vue";
import type { LocalDate } from "@js-joda/core";
import type { Vehicle } from "@/app/services/generated/vehicleService";
import type { UllageHeader } from "@/app/services/generated/ullageService";
import { Visit } from "@/app/services/generated/visitService";
import api from "@/app/api";
import { getById, isNotNullOrUndefined } from "@/app/apiutils";
import {
  DistributionConfiguration,
  distributionConfigurationService,
} from "@/app/services/generated/distributionconfigurationService";
import type { Outlet } from "@/app/services/generated/outletService";
import { outletService } from "@/app/services/generated/outletService";
import {
  makeDeepRef,
  makeRef,
  withLoading,
} from "@/components/composables/componentTools";
import apiCache from "@/app/caching/apiCache";
import DeliveryAreasCache from "@/app/caching/caches/deliveryAreasCache";
import SupplierManufacturersCache from "@/app/caching/caches/supplierManufacturersCache";
import Guid from "@/app/types/common/guid";
import { journeyService } from "@/app/services/generated/journeyv2Service";
import { journeyService as journeyServiceExtended } from "@/app/services/extensions/journeyServiceExtended";
import { useI18n } from "@/plugins/i18n";
import { useReactiveDialogSetup } from "@/components/mixins/dialogs";
import type { ValidationRule } from "@/plugins/validation";
import { ruleSet } from "@/plugins/validation";
import { despatchDateStringHighlighter } from "@/components/controls/input/mixins/BmDateHighlighter";
import { useFormValidator } from "@/components/mixins/validatable";
import BmAddress from "@/components/controls/address/BmAddress.vue";

interface Business {
  id: Guid;
  businessName: string;
  isHidden: boolean;
}

export interface AddVisitEvent {
  visit: Visit;
  vehicleId: Guid | null;
}

export default defineComponent({
  name: "AddVisitDialog",
  components: { BmDialog, BmAlert, BmTelephone, BmAddress },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    date: {
      type: Object as PropType<LocalDate>,
      required: false,
      default: () => tenantService.today,
    },
    vehicle: {
      type: Object as PropType<Readonly<Vehicle> | null>,
      required: false,
      default: () => null,
    },
    ullageHeader: {
      type: Object as PropType<UllageHeader>,
      required: false,
      default: () => null,
    },
    activeVehiclesToday: {
      type: Array as PropType<Readonly<Vehicle>[]>,
      required: false,
      default: () => [],
    },
    visitToEdit: {
      type: Object as PropType<Visit | null>,
      required: false,
      default: () => null,
    },
    // Outlet or Supplier Id
    businessId: {
      type: Object as PropType<Guid | null>,
      required: false,
      default: () => null,
    },
  },
  setup(props, context) {
    const { $m } = useI18n();
    const { validate } = useFormValidator();
    const { cancel, accept } = useReactiveDialogSetup(
      ref(props.value),
      context
    );
    const visit = makeDeepRef<Visit>(props.visitToEdit?.clone() ?? new Visit());
    const distributionConfiguration = ref<DistributionConfiguration>(
      new DistributionConfiguration()
    );
    const selectedVehicleId = ref<Guid | null>(props.vehicle?.id ?? null);
    const selectedBusinessId = ref<Guid | null>(
      props.visitToEdit?.outletId ?? null
    );
    const deliveryTimeErrors = ref<string[]>([]);
    const inProgressVisitIds = makeRef<readonly Guid[]>([]);
    const outlets = makeRef<readonly Outlet[]>([]);
    const cache = apiCache.buildCacheSet({
      supplierManufacturers: SupplierManufacturersCache,
      deliveryAreas: DeliveryAreasCache,
    });
    const showAddress = ref(!visit.value.outletId && !!visit.value.version);
    const allBusinesses = computed((): Business[] =>
      outlets.value
        .map(
          (outlet): Business => ({
            id: outlet.id,
            businessName: outlet.displayName,
            isHidden: outlet.isHidden,
          })
        )
        .concat(cache.supplierManufacturers.allReactive.value)
    );

    const loadPrerequisites = withLoading(async () => {
      [outlets.value, distributionConfiguration.value] = await Promise.all([
        outletService.getAllOutlets(api.tenantId),
        distributionConfigurationService.getDistributionConfiguration(
          api.tenantId
        ),
        cache.update(),
      ]);

      if (props.visitToEdit) {
        inProgressVisitIds.value = (
          await journeyService.getJourneyDetailsForDespatchDate(
            api.tenantId,
            tenantService.today
          )
        )
          .filter((journey) =>
            journeyServiceExtended.journeyInProgress(journey)
          )
          .flatMap((journey) => journey.orderedStopKeys)
          .map((stop) => stop.visitId)
          .filter(isNotNullOrUndefined);
      }
    });

    const selectOutletOrSupplier = (id: Guid | null) => {
      // In the case of clearing
      if (id === null) {
        visit.value.outletId = null;
        return;
      }

      const outlet = getById(outlets.value, id);

      // If what we selected is an outlet, set it as our outlet and update the address
      if (outlet) {
        visit.value.outletId = outlet.id;
        if (outlet.hasDefaultDeliveryAddress) {
          visit.value.businessName = outlet.displayName;
          visit.value.addressLine1 = outlet.defaultDeliveryAddressLine1;
          visit.value.addressLine2 = outlet.defaultDeliveryAddressLine2;
          visit.value.addressLine3 = outlet.defaultDeliveryAddressLine3;
          visit.value.addressLine4 = outlet.defaultDeliveryAddressLine4;
          visit.value.addressLine5 = outlet.defaultDeliveryAddressLine5;
          visit.value.addressLine6 = outlet.defaultDeliveryAddressLine6;
          visit.value.addressPostcode = outlet.defaultDeliveryAddressPostcode;
        } else {
          visit.value.businessName = outlet.displayName;
          visit.value.addressLine1 = outlet.mainAddressLine1;
          visit.value.addressLine2 = outlet.mainAddressLine2;
          visit.value.addressLine3 = outlet.mainAddressLine3;
          visit.value.addressLine4 = outlet.mainAddressLine4;
          visit.value.addressLine5 = outlet.mainAddressLine5;
          visit.value.addressLine6 = outlet.mainAddressLine6;
          visit.value.addressPostcode = outlet.mainAddressPostcode;
        }
        visit.value.contactName = outlet.defaultDeliveryRecipient;
        visit.value.telephoneNumber =
          outlet.defaultDeliveryTelephoneNumber || outlet.telephoneNumber;
        visit.value.startTime = outlet.deliveryTimeStart;
        visit.value.endTime = outlet.deliveryTimeEnd;
        return;
      }

      const supplier = cache.supplierManufacturers.getById(id);

      // Otherwise we should have found a supplier
      if (supplier) {
        visit.value.outletId = null;
        visit.value.businessName = supplier.businessName;
        visit.value.addressLine1 = supplier.addressLine1;
        visit.value.addressLine2 = supplier.addressLine2;
        visit.value.addressLine3 = supplier.addressLine3;
        visit.value.addressLine4 = supplier.addressLine4;
        visit.value.addressLine5 = supplier.addressLine5;
        visit.value.addressLine6 = supplier.addressLine6;
        visit.value.addressPostcode = supplier.postcode;
        visit.value.telephoneNumber = supplier.phoneNumber;
        visit.value.contactName = supplier.contactName;
        return;
      }
    };

    const validateAndAccept = () => {
      if (visit.value.version === 0) {
        visit.value.id = Guid.create();
      }

      const isVehicleAvailable = props.activeVehiclesToday.some((vehicle) =>
        vehicle.id.equals(selectedVehicleId.value!)
      );

      // Remove the vehicle if it's not available, or we aren't looking at today
      if (
        !isVehicleAvailable ||
        !tenantService.today.equals(visit.value.date)
      ) {
        selectedVehicleId.value = null;
      }

      accept({
        visit: visit.value,
        vehicleId: selectedVehicleId.value,
      } as AddVisitEvent);
    };

    onBeforeMount(async () => {
      await loadPrerequisites();

      if (props.visitToEdit) {
        selectedBusinessId.value = props.visitToEdit.outletId;
        return;
      }
      // Set the visit's date to the provided
      visit.value.date = props.date;

      // Map ullage fields to visit
      if (props.ullageHeader) {
        selectedBusinessId.value = props.ullageHeader.outletId;
        visit.value.date = props.ullageHeader.pickupDate || tenantService.today;
        visit.value.startTime = props.ullageHeader.pickupWindowStart;
        visit.value.endTime = props.ullageHeader.pickupWindowEnd;
        visit.value.stopMinutes = props.ullageHeader.pickupStopMinutes;
        visit.value.reason = props.ullageHeader.pickupNotesForDriver;
        selectOutletOrSupplier(props.ullageHeader.outletId!);
      }

      if (props.businessId) {
        selectedBusinessId.value = props.businessId;
        selectOutletOrSupplier(props.businessId); // Set the business to visit if exists
      }

      if (visit.value.stopMinutes === 0 && visit.value.version === 0) {
        visit.value.stopMinutes =
          distributionConfiguration.value.defaultStopMinutes;
      }
    });

    const dialogLabels = computed(() => {
      if (props.ullageHeader) {
        return {
          title: $m.ullages_add_pickup,
          textArea: $m.keys.ullages_notes_to_driver,
          timeFrom: $m.keys.ullages_pickup_time_from,
          timeTo: $m.keys.ullages_pickup_time_to,
        };
      } else {
        return {
          title: props.visitToEdit
            ? $m.distribution_edit_visit
            : $m.distribution_add_visit,
          textArea: $m.keys.distribution_reason,
          timeFrom: $m.keys.distribution_visit_time_from,
          timeTo: $m.keys.distribution_visit_time_to,
        };
      }
    });

    const showVehicleMenu = computed(() => {
      return (
        visit.value.date.equals(tenantService.today) &&
        props.activeVehiclesToday.length
      );
    });

    const visitIsOnActiveJourney = computed(() => {
      return inProgressVisitIds.value.some((id) => id.equals(visit.value.id));
    });

    const validOutletSupplierOrAddressRule = computed((): ValidationRule => {
      const validAddress =
        visit.value.businessName &&
        visit.value.addressLine1 &&
        visit.value.addressPostcode;

      return validAddress || visit.value.outletId
        ? () => true
        : ruleSet.required(
            true,
            $m.keys.distribution_outlet_supplier_address_rule
          );
    });

    const deliveryTimeChange = () => {
      deliveryTimeErrors.value = [];
      // If both values exist and there is a mismatch (start is after end) add error
      if (
        visit.value.endTime &&
        visit.value.startTime?.isAfter(visit.value.endTime)
      ) {
        deliveryTimeErrors.value.push($m.distribution_delivery_times_mismatch);
      }
      // XOR to check that only one of the values is null/ undefined
      else if (
        visit.value.endTime ? !visit.value.startTime : visit.value.startTime
      ) {
        deliveryTimeErrors.value.push(
          $m.distribution_delivery_times_mismatch_empty
        );
      }
    };

    const isValidDeliveryEvent = (date: string): boolean | string => {
      if (visit.value.outletId) {
        const outletDeliveryArea = getById(
          outlets.value,
          visit.value.outletId
        )?.deliveryAreaId;

        if (outletDeliveryArea) {
          const deliveryArea = cache.deliveryAreas.getById(outletDeliveryArea);

          return deliveryArea
            ? despatchDateStringHighlighter(date, deliveryArea.despatchDays, 0) // No Lead Time for Visits
            : false;
        }
      }
      return false;
    };

    watch(
      () => [visit.value.startTime, visit.value.endTime],
      deliveryTimeChange
    );

    return {
      dialogLabels,
      visitIsOnActiveJourney,
      allBusinesses,
      selectedBusinessId,
      selectedVehicleId,
      visit,
      showAddress,
      showVehicleMenu,
      today: tenantService.today,
      deliveryTimeErrors,

      validOutletSupplierOrAddressRule,
      selectOutletOrSupplier,
      deliveryTimeChange,
      isValidDeliveryEvent,
      validateAndAccept,
      cancel,
    };
  },
});
</script>
